"use client";

import { useQuery } from "@tanstack/react-query";
import { getMomentusOptionsConfig, type MomentusOptionsSnapshot } from "@/lib/momentusOptions";
import { isSelectOtherEnabled } from "@/lib/selectOtherOption";
import type { SurveyQuestion } from "@/types/survey";
import { apiRequest } from "@/utils/api";
import { momentusStateKey } from "@/app/settings/integrations/momentusIntegrationUi";

export const MOMENTUS_TIMEZONE_OPTIONS = ["UTC", ...Intl.supportedValuesOf("timeZone")].map((value) => ({
  value, label: value.replaceAll("_", " "),
}));

export function useMomentusOptionsState(surveyId: string) {
  return useQuery({
    queryKey: [...momentusStateKey, "options-state", surveyId],
    queryFn: () => apiRequest<{ available: boolean; timezone: string | null }>(`/api/surveys/${surveyId}/momentus-options`),
    staleTime: 30_000,
    retry: false,
  });
}

export function useMomentusOptionsPreview(question: SurveyQuestion, enabled = true) {
  const config = getMomentusOptionsConfig(question);
  return useQuery({
    queryKey: [...momentusStateKey, "options-preview", question.survey, question.id, config, isSelectOtherEnabled(question)],
    queryFn: () => apiRequest<MomentusOptionsSnapshot>(`/api/surveys/${question.survey}/momentus-options`, {
      method: "POST",
      body: JSON.stringify({ questionId: question.id, config, allowOther: isSelectOtherEnabled(question) }),
    }),
    enabled: enabled && Boolean(question.survey && config?.enabled),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}
